/**
 * Captcha integration for Knox SPA.
 *
 * Two backends, switched by the IS_CN runtime flag (window.__KNOX_CONFIG__.IS_CN):
 *   - overseas (IS_CN=false): Google reCAPTCHA Enterprise — score-based, dynamic
 *     script load + per-action execute.
 *   - china   (IS_CN=true):  Aliyun Captcha 2.0 — invisible mode, initAliyunCaptcha
 *     binds to a hidden trigger button; on success it yields a captchaVerifyParam
 *     string forwarded verbatim to the backend (X-Recaptcha-Token header).
 *
 * Callers use the unified loadCaptcha()/executeCaptcha(action) entry points and
 * stay ignorant of which backend is active. The token contract is identical:
 * executeCaptcha resolves to a string sent as X-Recaptcha-Token.
 *
 * Overseas behaviour is byte-for-byte unchanged when IS_CN is not set.
 */

/** Singleton promise to avoid loading the script multiple times. */
let loadPromise: Promise<void> | null = null;
/** The site key used to load the script. */
let loadedSiteKey: string | null = null;

/**
 * Dynamically load the reCAPTCHA Enterprise JS SDK.
 * Safe to call multiple times — subsequent calls resolve immediately if already loaded.
 *
 * @param siteKey - reCAPTCHA Enterprise score-based site key
 */
export async function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (loadedSiteKey === siteKey && loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    // Check if already loaded (e.g. script was added externally)
    if (window.grecaptcha?.enterprise) {
      loadedSiteKey = siteKey;
      window.grecaptcha.enterprise.ready(() => resolve());
      return;
    }

    // Check if script tag already exists (avoid duplicates)
    const existing = document.querySelector(`script[src*="${siteKey}"]`);
    if (existing) {
      // Script is loading, wait for grecaptcha to be available
      const poll = setInterval(() => {
        if (window.grecaptcha?.enterprise) {
          clearInterval(poll);
          loadedSiteKey = siteKey;
          window.grecaptcha.enterprise.ready(() => resolve());
        }
      }, 50);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha!.enterprise!.ready(() => {
        loadedSiteKey = siteKey;
        resolve();
      });
    };

    script.onerror = () => {
      loadPromise = null; // Allow retry
      reject(new Error('Failed to load reCAPTCHA script'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Execute a reCAPTCHA assessment for the given action.
 * Must call loadRecaptchaScript() before this function.
 *
 * @param action - reCAPTCHA action name (e.g. "login", "register", "check_email")
 * @returns reCAPTCHA token string to be sent to the backend
 */
export async function executeRecaptcha(action: string): Promise<string> {
  if (!window.grecaptcha?.enterprise || !loadedSiteKey) {
    throw new Error('reCAPTCHA not loaded');
  }
  const token = await window.grecaptcha.enterprise.execute(loadedSiteKey, { action });
  return token;
}

// ─────────────────────────────────────────────────────────────────────────────
// Aliyun Captcha 2.0 (China) — invisible mode per official V3 guide:
// initAliyunCaptcha registers a success callback; the caller triggers
// verification via captcha.startTracelessVerification(). The SDK decides
// silently (no UI) for trusted users or pops a slider for risky ones, then
// fires success with captchaVerifyParam — which we forward as the token.
// ─────────────────────────────────────────────────────────────────────────────

interface AliyunCaptchaConfig {
  region: string; // "cn" | "sgp"
  prefix: string; // 身份标 from Aliyun console
}

interface AliyunCaptchaInstance {
  startTracelessVerification?: () => void;
  show?: () => void;
  hide?: () => void;
  refresh?: () => void;
  reset?: () => void;
}
interface AliyunWindow {
  AliyunCaptchaConfig?: AliyunCaptchaConfig;
  initAliyunCaptcha?: (opts: {
    SceneId: string;
    mode: string;
    element: string;
    button: string;
    success: (captchaVerifyParam: string) => void;
    fail?: () => void;
    getInstance?: (instance: AliyunCaptchaInstance) => void;
    slideStyle?: { width: number; height: number };
    language?: string;
    delayBeforeSuccess?: boolean;
  }) => void;
}

/** Render container id for Aliyun's popup overlay (must exist in DOM at init). */
const ALIYUN_ELEMENT_ID = 'knox-aliyun-captcha-element';
/** Hidden trigger button id — required by initAliyunCaptcha's `button` param. */
const ALIYUN_TRIGGER_ID = 'knox-aliyun-captcha-trigger';

let aliyunInstance: AliyunCaptchaInstance | null = null;
let aliyunSceneId: string | null = null;
/**
 * When the current instance was handed back by getInstance. Aliyun's SDK
 * desyncs after ~10 min of idle (startTracelessVerification fires fail-then-
 * success out of order, dropping the token — the "no response after a long
 * pause" bug). We re-init before that window so a stale instance is never used.
 */
let aliyunInstanceAt = 0;
const ALIYUN_INSTANCE_MAX_AGE_MS = 8 * 60_000;
/**
 * Last opts passed to loadAliyunCaptcha, plus the in-flight load promise.
 * executeAliyunCaptcha uses these to await SDK readiness on the race where the
 * user submits before the mount-time loadCaptcha finished — instead of failing
 * open with no token, it waits for the same load to complete, then the instance
 * is ready and the slider pops normally.
 */
let lastAliyunOpts: { region: string; prefix: string; sceneId: string } | null = null;
let aliyunLoadPromise: Promise<void> | null = null;
/** Resolves the next executeAliyunCaptcha() promise when success fires. */
let pendingResolver: ((param: string) => void) | null = null;
let pendingRejecter: ((err: Error) => void) | null = null;
/**
 * Last successful token + timestamp. Aliyun's SDK fails the SECOND call to
 * startTracelessVerification within a short window (treats rapid repeats as
 * bot behavior) — but the token itself is valid for the backend's
 * VerifyIntelligentCaptcha for a brief period. So we reuse it across actions
 * (e.g. check-email → forgot-password) instead of re-triggering the SDK.
 */
let cachedToken: string | null = null;
let cachedTokenAt = 0;
// Aliyun's VerifyIntelligentCaptcha accepts a token for ~10 minutes after it's
// issued, and the SDK fails/gets flaky if startTracelessVerification is re-
// triggered within a session (the "no popup / no response" bug). So we cache the
// token long enough to cover a full login/forgot-password flow (check-email →
// login, or check-email → forgot-password) without ever re-triggering the SDK.
// 5 min stays safely inside the 10-min server validity window.
const TOKEN_CACHE_MS = 5 * 60_000;
// Must be long enough for the user to complete the slider — 15s was too short,
// causing the login request to fire (without token) BEFORE the slider finished.
const VERIFY_TIMEOUT_MS = 60_000;

/**
 * Load the Aliyun SDK and init an invisible-mode verifier. Idempotent, but
 * re-inits if the trigger button left the DOM (SPA route change unmounts the
 * body child the SDK bound to). The success callback resolves whichever
 * executeAliyunCaptcha() turn is waiting.
 */
export async function loadAliyunCaptcha(opts: {
  region: string;
  prefix: string;
  sceneId: string;
}): Promise<void> {
  const triggerMounted = !!document.getElementById(ALIYUN_TRIGGER_ID);
  if (aliyunInstance && aliyunSceneId === opts.sceneId && triggerMounted) return;

  // A load is already in flight for these opts — await it instead of starting a
  // second init (two concurrent initAliyunCaptcha calls corrupt SDK state).
  if (aliyunLoadPromise) {
    return aliyunLoadPromise;
  }

  lastAliyunOpts = opts;
  aliyunLoadPromise = (async () => {
    // Reset stale state from a previous route/scene.
    aliyunInstance = null;
    aliyunSceneId = null;

    (window as unknown as { AliyunCaptchaConfig?: AliyunCaptchaConfig }).AliyunCaptchaConfig = {
      region: opts.region,
      prefix: opts.prefix,
    };
    ensureAliyunDomNodes();
    await injectAliyunScript();

    const initFn = (window as unknown as AliyunWindow).initAliyunCaptcha;
    if (!initFn) throw new Error('Aliyun captcha SDK failed to expose initAliyunCaptcha');

    // Wait for getInstance to hand back the captcha instance before resolving —
    // executeAliyunCaptcha needs the instance (for startTracelessVerification),
    // and getInstance fires asynchronously after init. Resolving too early makes
    // a same-tick executeAliyunCaptcha see instance=null.
    await new Promise<void>((resolve) => {
      const initTimer = setTimeout(() => {
        // getInstance didn't fire in time — resolve anyway so a later execute can
        // surface a clear "not initialized" error instead of hanging loadCaptcha.
        resolve();
      }, 8000);
      initFn({
        SceneId: opts.sceneId,
        mode: 'popup',
        element: `#${ALIYUN_ELEMENT_ID}`,
        button: `#${ALIYUN_TRIGGER_ID}`,
        delayBeforeSuccess: false,
        success: (captchaVerifyParam: string) => {
          // Cache the token unconditionally first. After a long idle (10+ min)
          // the SDK's internal state can desync — it may fire success when no
          // turn is waiting (pendingResolver null), which previously dropped
          // the token and left the awaiting request hanging. Caching ensures
          // the next executeCaptcha() reuses it even if this callback raced.
          if (captchaVerifyParam) {
            cachedToken = captchaVerifyParam;
            cachedTokenAt = Date.now();
            console.log('[aliyun-captcha] success callback fired, param length=', captchaVerifyParam.length, '(cached for', TOKEN_CACHE_MS / 1000 + 's)', '| resolver present:', !!pendingResolver);
          }
          if (pendingResolver) {
            const resolveFn = pendingResolver;
            pendingResolver = null;
            pendingRejecter = null;
            resolveFn(captchaVerifyParam);
          }
          // SDK doesn't always close its popup on success either — remove the
          // mask defensively so it can't trap the UI on the next interaction.
          cleanupAliyunPopup();
        },
        fail: () => {
          // Aliyun's fail callback (onBizFail) is NOT a terminal failure — it
          // fires when traceless verification needs to downgrade to an interactive
          // slider. Resolving here was the bug: it released the awaiting request
          // with no token, and by the time the slider's real success fired,
          // pendingResolver was already null → "success but no request sent".
          // Instead: clear the popup mask (so the downgrade slider the SDK shows
          // next is fresh) and KEEP WAITING for the real success/timeout.
          console.log('[aliyun-captcha] fail callback (traceless downgrade) — keep waiting for slider result');
          cleanupAliyunPopup();
        },
        getInstance: (instance: AliyunCaptchaInstance) => {
          aliyunInstance = instance;
          aliyunInstanceAt = Date.now();
          clearTimeout(initTimer);
          resolve();
        },
        language: 'cn',
      });
    });

    aliyunSceneId = opts.sceneId;
  })();

  try {
    await aliyunLoadPromise;
  } finally {
    aliyunLoadPromise = null;
  }
}

/**
 * Trigger the Aliyun invisible verification and resolve with captchaVerifyParam
 * on success (silent pass for trusted users, slider for risky ones). Uses the
 * official startTracelessVerification() API — not a hidden-button click hack.
 *
 * Race handling: if the SDK hasn't finished initializing when this is called
 * (the mount-time loadCaptcha is still in flight), await loadCaptcha first so
 * the instance is ready and the slider pops normally. Only if the SDK genuinely
 * can't initialize (script blocked / getInstance never fires) does this resolve
 * undefined — callers forward it, the backend rejects, and the user can retry.
 */
export async function executeAliyunCaptcha(): Promise<string | undefined> {
  // Reuse a recent token across actions. Aliyun's SDK rejects rapid repeat
  // startTracelessVerification calls (fail callback, treats as bot), so a
  // check-email → forgot-password sequence can't trigger the SDK twice in a
  // row. The token is still valid for the backend's verify call within
  // TOKEN_CACHE_MS, so reuse it instead of re-triggering.
  if (cachedToken && Date.now() - cachedTokenAt < TOKEN_CACHE_MS) {
    console.log('[aliyun-captcha] reusing cached token, age=', Date.now() - cachedTokenAt, 'ms');
    return cachedToken;
  }

  console.log('[aliyun-captcha] executeAliyunCaptcha called, instance=', !!aliyunInstance, 'hasSTV=', !!aliyunInstance?.startTracelessVerification);

  // Re-init if the instance is missing OR stale. Aliyun's SDK desyncs after a
  // long idle (~10 min): startTracelessVerification then fires fail-then-success
  // out of order, dropping the token — the "no response after a long pause" bug.
  // Re-initializing before the staleness window guarantees a fresh instance.
  const instanceStale = aliyunInstanceAt > 0 && Date.now() - aliyunInstanceAt > ALIYUN_INSTANCE_MAX_AGE_MS;
  if (!aliyunInstance?.startTracelessVerification || instanceStale) {
    if (lastAliyunOpts) {
      if (instanceStale) {
        console.log('[aliyun-captcha] instance stale (age=', Date.now() - aliyunInstanceAt, 'ms), re-initializing...');
        // Force re-init: clear the cached instance so loadAliyunCaptcha re-runs.
        aliyunInstance = null;
        aliyunSceneId = null;
      } else {
        console.log('[aliyun-captcha] instance not ready, awaiting loadCaptcha...');
      }
      await loadAliyunCaptcha(lastAliyunOpts).catch(() => {});
    }
  }
  if (!aliyunInstance?.startTracelessVerification) {
    // Still not ready after load (SDK genuinely failed). Return undefined — the
    // backend rejects the tokenless request and the user can retry. DO NOT throw
    // (the throw is swallowed by callers and freezes the submit button).
    console.warn('[aliyun-captcha] not initialized after load — returning undefined');
    return undefined;
  }
  if (pendingResolver) {
    console.warn('[aliyun-captcha] a turn already in progress — returning undefined');
    return undefined;
  }
  return new Promise<string | undefined>((resolve) => {
    // Timeout: the SDK sometimes never calls back (no success, no fail) when
    // startTracelessVerification is invoked in a bad state. Avoid hanging forever.
    // All failure/timeout paths resolve(undefined) (fail-open) so the request
    // still goes out and the backend's NEED_CHALLENGE + CN checkbox fallback
    // can recover — reject would be swallowed by callers and freeze the button.
    const timer = setTimeout(() => {
      if (pendingRejecter) {
        console.warn('[aliyun-captcha] TIMEOUT: no callback within', VERIFY_TIMEOUT_MS, 'ms');
        pendingResolver = null;
        pendingRejecter = null;
        // SDK never closed its popup — force-remove the mask so it doesn't trap
        // the UI underneath (the "no response after slider" bug).
        cleanupAliyunPopup();
        // Fail-open on timeout too — let NEED_CHALLENGE fallback handle it
        // rather than rejecting (reject was also swallowed → stuck button).
        resolve(undefined);
      }
    }, VERIFY_TIMEOUT_MS);

    pendingResolver = (param) => {
      clearTimeout(timer);
      // Cache the token so subsequent actions (login/forgot-password after
      // check-email) reuse it instead of re-triggering the SDK.
      cachedToken = param;
      cachedTokenAt = Date.now();
      console.log('[aliyun-captcha] success callback fired, param length=', param?.length, '(cached for', TOKEN_CACHE_MS / 1000 + 's)');
      resolve(param);
    };
    pendingRejecter = (err) => {
      // Defensive: Aliyun's fail callback no longer routes here (it only clears
      // the mask and keeps waiting — see loadAliyunCaptcha). This resolver is
      // kept as a backstop in case some other SDK path surfaces an error; it
      // fail-opens with undefined so the request isn't stranded.
      clearTimeout(timer);
      console.warn('[aliyun-captcha] pendingRejecter invoked (terminal fail):', err.message);
      cleanupAliyunPopup();
      resolve(undefined);
    };
    console.log('[aliyun-captcha] calling startTracelessVerification()');
    try {
      aliyunInstance!.startTracelessVerification!();
    } catch (e) {
      clearTimeout(timer);
      pendingResolver = null;
      pendingRejecter = null;
      console.error('[aliyun-captcha] startTracelessVerification threw:', e);
      cleanupAliyunPopup();
      resolve(undefined);
    }
  });
}

function ensureAliyunDomNodes(): void {
  if (!document.getElementById(ALIYUN_TRIGGER_ID)) {
    const btn = document.createElement('button');
    btn.id = ALIYUN_TRIGGER_ID;
    btn.type = 'button';
    btn.style.display = 'none';
    document.body.appendChild(btn);
  }
  if (!document.getElementById(ALIYUN_ELEMENT_ID)) {
    const el = document.createElement('div');
    el.id = ALIYUN_ELEMENT_ID;
    el.style.display = 'none';
    document.body.appendChild(el);
  }
}

/**
 * Remove the Aliyun SDK's popup overlay (#aliyunCaptcha-mask + window) from the
 * DOM. The SDK leaves these behind when verification fails, times out, or the
 * user closes the slider — and the mask covers the full viewport (1440x900),
 * intercepting pointer events and freezing every button beneath it. This is the
 * "no response after triggering the slider" production bug: the captcha Promise
 * resolves, the request goes out, but the residual mask traps the user.
 *
 * The SDK does not reliably clean up itself, so we do it defensively on every
 * terminal state (success/fail/timeout). Safe to call when no overlay exists.
 */
function cleanupAliyunPopup(): void {
  if (typeof document === 'undefined') return;
  const ids = ['aliyunCaptcha-mask', 'aliyunCaptcha-window-popup'];
  let removed = 0;
  ids.forEach((id) => {
    const el = document.getElementById(id);
    if (el?.parentNode) {
      el.parentNode.removeChild(el);
      removed++;
    }
  });
  // Log every call (even no-op) so we can confirm timing in the console.
  console.log('[aliyun-captcha] cleanupAliyunPopup: removed', removed, 'of', ids.length, '| mask still present:', !!document.getElementById('aliyunCaptcha-mask'));
}

function injectAliyunScript(): Promise<void> {
  const SCRIPT_ID = 'aliyun-captcha-sdk';
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if ((window as unknown as AliyunWindow).initAliyunCaptcha) {
        resolve();
        return;
      }
      existing.remove();
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Aliyun captcha SDK'));
    document.head.appendChild(s);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Unified entry points (callers use these — backend chosen by IS_CN at runtime)
// ─────────────────────────────────────────────────────────────────────────────

/** Reads the IS_CN flag from the runtime config injected by docker-entrypoint. */
export function isCnCaptcha(): boolean {
  return (window as unknown as { __KNOX_CONFIG__?: { IS_CN?: boolean } }).__KNOX_CONFIG__?.IS_CN === true;
}

interface RuntimeCaptchaConfig {
  /** Overseas: Google score site key. */
  scoreSiteKey?: string;
  /** China: Aliyun scene id, region, prefix. */
  aliyun?: { sceneId: string; region: string; prefix: string };
}

export type { RuntimeCaptchaConfig };

/**
 * Load whichever captcha backend the runtime IS_CN flag selects.
 * Call once on mount of any page that submits a captcha-protected action.
 */
export async function loadCaptcha(cfg: RuntimeCaptchaConfig): Promise<void> {
  if (isCnCaptcha()) {
    if (!cfg.aliyun?.sceneId) {
      throw new Error('IS_CN=true but Aliyun captcha config missing (sceneId)');
    }
    await loadAliyunCaptcha({
      region: cfg.aliyun.region || 'cn',
      prefix: cfg.aliyun.prefix,
      sceneId: cfg.aliyun.sceneId,
    });
    return;
  }
  if (!cfg.scoreSiteKey) {
    throw new Error('reCAPTCHA score site key missing');
  }
  await loadRecaptchaScript(cfg.scoreSiteKey);
}

/**
 * Execute a captcha challenge and resolve to the verification token.
 * For Google this is a per-action execute; for Aliyun it triggers the invisible
 * verifier (the action name is unused — Aliyun carries the scene via SceneId).
 *
 * Returns undefined when no token is available (Aliyun SDK not yet initialized
 * or verification failed-open). Callers forward undefined; for write ops the
 * backend returns NEED_CHALLENGE, which the checkbox fallback (overseas) or
 * Aliyun re-verify fallback (CN) handles.
 */
export async function executeCaptcha(_action: string): Promise<string | undefined> {
  if (isCnCaptcha()) {
    return executeAliyunCaptcha();
  }
  return executeRecaptcha(_action);
}

// Extend Window interface for grecaptcha
declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
        render?: (container: HTMLElement, options: Record<string, unknown>) => number;
        reset?: (widgetId?: number) => void;
      };
      render?: (container: HTMLElement, options: Record<string, unknown>) => number;
      reset?: (widgetId?: number) => void;
    };
  }
}
